import React, { useState, useEffect } from "react";
import { useTranslations } from "use-intl";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { useNavigate } from "react-router-dom";
import { Invoice } from "@/types/payments";
import { ReviewItem } from "@/types/review";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, AlertTriangle, CheckCircle2, FileText, ExternalLink, Trash2 } from "lucide-react";
import { useFormatRelativeDate } from "@/i18n/useFormatRelativeDate";
import { describeDuplicate, type DuplicateInfo } from "./duplicateMessage";
import { formatAmount } from "@financialharmony/ui";

// Where each finalized document type lives; its label is `consultantUpload.documentKinds.<type>`.
const DOCUMENT_TYPE_ROUTES = {
  payable: "/account-payables",
  receivable: "/account-receivables",
  receipt: "/receipts",
  expense: "/expenses",
  sales_report: "/sales-reports",
} as const;
type DocumentKind = keyof typeof DOCUMENT_TYPE_ROUTES;

function isDocumentKind(type: string | null | undefined): type is DocumentKind {
  return !!type && type in DOCUMENT_TYPE_ROUTES;
}

interface DraftListProps {
  drafts: Invoice[];
  loading?: boolean;
  mode: "processing" | "needs-input" | "completed" | "finalized";
  onDraftClick?: (draft: Invoice) => void;
  onFinalizedClick?: (draft: Invoice) => void;
  onDelete?: (draftId: string) => Promise<void>;
}

const DraftList: React.FC<DraftListProps> = ({ drafts, loading, mode, onDraftClick, onFinalizedClick, onDelete }) => {
  const t = useTranslations("consultantUpload.list");
  const kinds = useTranslations("consultantUpload.documentKinds");
  const formatRelativeDate = useFormatRelativeDate();
  const navigate = useNavigate();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId || !onDelete) return;
    setDeleteLoading(true);
    try {
      await onDelete(confirmDeleteId);
    } finally {
      setDeleteLoading(false);
      setConfirmDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">{t("loading")}</span>
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center">
        {t("empty")}
      </p>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {drafts.map((draft) => (
          <Card
            key={draft.id}
            className={
              mode === "needs-input" || mode === "finalized"
                ? "cursor-pointer hover:bg-muted/50 transition-colors"
                : ""
            }
            onClick={
              mode === "needs-input"
                ? () => onDraftClick?.(draft)
                : mode === "finalized"
                ? () => onFinalizedClick?.(draft)
                : undefined
            }
          >
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {draft.fileName || t("unnamed")}
                  </p>
                  {draft.documentType && (
                    <p className="text-xs text-muted-foreground capitalize">
                      {isDocumentKind(draft.documentType) ? kinds(draft.documentType) : draft.documentType}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                {(draft.draftCreatedAt || draft.createdAt) && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatRelativeDate(
                      (mode === "processing" || mode === "needs-input")
                        ? (draft.draftCreatedAt || draft.createdAt!)
                        : (draft.createdAt || draft.draftCreatedAt!)
                    )}
                  </span>
                )}

                {mode === "processing" && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t("extracting")}
                  </Badge>
                )}

                {mode === "needs-input" && (
                  <>
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {t("needsReview")}
                    </Badge>
                    <Button size="sm" variant="outline" onClick={(e) => {
                      e.stopPropagation();
                      onDraftClick?.(draft);
                    }}>
                      {t("review")}
                    </Button>
                  </>
                )}

                {mode === "completed" && (
                  <>
                    <div className="text-sm text-right">
                      {draft.vendor && (
                        <p className="font-medium">{draft.vendor}</p>
                      )}
                      {draft.amount != null && (
                        <p className="text-muted-foreground">
                          {formatAmount(draft.amount)} {draft.currency}
                        </p>
                      )}
                    </div>
                    <Badge variant="secondary" className="flex items-center gap-1 text-green-700 bg-green-100">
                      <CheckCircle2 className="h-3 w-3" />
                      {t("extracted")}
                    </Badge>
                  </>
                )}

                {mode === "finalized" && (() => {
                  const kind = isDocumentKind(draft.documentType) ? draft.documentType : null;
                  return (
                    <>
                      <div className="text-sm text-right">
                        {draft.vendor && (
                          <p className="font-medium">{draft.vendor}</p>
                        )}
                        {draft.amount != null && (
                          <p className="text-muted-foreground">
                            {formatAmount(draft.amount)} {draft.currency}
                          </p>
                        )}
                      </div>
                      {kind && (
                        <Badge variant="outline" className="capitalize">
                          {kinds(kind)}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="flex items-center gap-1 text-green-700 bg-green-100">
                        <CheckCircle2 className="h-3 w-3" />
                        {t("done")}
                      </Badge>
                      {kind && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(DOCUMENT_TYPE_ROUTES[kind]);
                          }}
                        >
                          <ExternalLink className="h-3 w-3" />
                          {t("view")}
                        </Button>
                      )}
                    </>
                  );
                })()}

                {draft.isDraft && onDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteId(draft.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} disabled={deleteLoading}>
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DraftList;

interface RejectedSectionProps {
  rejected: Invoice[];
  onView?: (documentId: string) => void;
  onMarkedSeen?: () => void;
}

export const RejectedSection: React.FC<RejectedSectionProps> = ({ rejected, onView, onMarkedSeen }) => {
  const t = useTranslations("consultantUpload.list");
  const progress = useTranslations("consultantUpload.progress");
  const duplicate = useTranslations("consultantUpload.duplicate");
  useEffect(() => {
    if (rejected.length === 0) return;
    const ids = rejected.map((d) => d.id);
    const service = ServiceRegistry.getInvoiceUploadService();
    service
      .markRejectedSeen(ids)
      .then(() => onMarkedSeen?.())
      .catch((err) => console.error('Failed to mark rejected docs as seen:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejected]);

  if (rejected.length === 0) return null;
  return (
    <div className="space-y-2">
      {rejected.map((doc) => {
        let duplicateInfo: DuplicateInfo | null = null;
        let rejectionMessage: string | null = null;
        try {
          const raw = (doc.extractionMetadata as any)?.finalizationError;
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed.reason === 'DUPLICATE_DOCUMENT') duplicateInfo = parsed.duplicateOf;
            else if (parsed.reason === 'POOR_IMAGE_QUALITY') rejectionMessage = progress("poorImage");
            else rejectionMessage = parsed.reason ?? null;
          }
        } catch { /* ignore parse errors */ }

        const duplicateMessage = duplicateInfo ? describeDuplicate(duplicateInfo, duplicate) : null;

        return (
          <Card key={doc.id} data-testid="rejected-document-row" className="border-destructive/30 bg-destructive/5">
            <CardContent className="flex items-center justify-between py-3 px-4">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {doc.fileName || t("unnamed")}
                  </p>
                  <p className="text-xs text-destructive">
                    {duplicateMessage ?? rejectionMessage ?? (doc as { rejectionReason?: string }).rejectionReason ?? t("rejected")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {onView && (
                  <Button variant="outline" size="sm" onClick={() => onView(doc.id)}>
                    {t("view")}
                  </Button>
                )}
                <Badge variant="destructive" className="text-xs">{t("rejected")}</Badge>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

interface NeedsReviewSectionProps {
  items: ReviewItem[];
  onHandleManually: (item: ReviewItem) => void;
}

export const NeedsReviewSection: React.FC<NeedsReviewSectionProps> = ({ items, onHandleManually }) => {
  const t = useTranslations("consultantUpload.list");
  if (items.length === 0) return null;
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <Card key={item.id} className="border-warning/30 bg-warning/5">
          <CardContent className="flex items-center justify-between py-3 px-4">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {item.fileName || item.title || t("unnamed")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("unsupported")}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onHandleManually(item)}
            >
              {t("handleManually")}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
